import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ISupplierUserProfile} from 'constants/interfaces';
import {apiConfirmUserPassword, apiUpdateWalletPin} from 'constants/Api';
import IconButton from '@material-ui/core/IconButton';
import {Visibility, VisibilityOff} from '@material-ui/icons';

export function UpdateWalletPinCodeDialog(props: {
  user: ISupplierUserProfile;
  open: boolean;
  onConfirm: () => any;
  onCancel: () => any;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (submission: UpdateWalletPinCodeSubmission) => {
    setIsLoading(true);
    apiConfirmUserPassword(submission.password)
      .then(async (passwordResponse) => {
        if (!passwordResponse.success) {
          alert(passwordResponse.message);
          return;
        }
        const pinResponse = await apiUpdateWalletPin(submission.pin);
        if (!pinResponse.success) {
          alert(pinResponse.message);
          return;
        }
        props.onConfirm();
      })
      .catch((err) => {
        console.log('Failed to update wallet pin', err);
        alert('Unable to update pin at the moment. Please try again later');
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onCancel}
      className="update-password-dialog"
    >
      <DialogTitle>Update Wallet Pin Code</DialogTitle>
      <DialogContent>
        <UpdateWalletPinCodeForm
          isLoading={isLoading}
          submitButtonTitle={'Update'}
          onSubmit={handleSubmit}
          cancelButtonTitle={'Cancel'}
          onCancel={props.onCancel}
        />
      </DialogContent>
    </Dialog>
  );
}

export type UpdateWalletPinCodeSubmission = {
  password: string;
  pin: string;
};

export function UpdateWalletPinCodeForm(props: {
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: UpdateWalletPinCodeSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) {
  type Submission = UpdateWalletPinCodeSubmission & {
    confirmPin: string;
  };

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, getValues} = useForm<Submission>();

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      password: submission.password,
      pin: submission.pin,
    });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Controller
                name="password"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Password"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Password required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="pin"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Pin"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Pin required',
                  minLength: {
                    value: 4,
                    message: 'Pin should contain at least 4 characters',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="confirmPin"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Confirm Pin"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Pin required',
                  validate: (value) =>
                    value === getValues('pin') || 'Pin codes do not match',
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{paddingTop: '1rem', paddingBottom: '1rem'}}
          >
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className={'primary-btn'}
                type="submit"
                variant="contained"
                disableElevation
                fullWidth
                color="primary"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
